import { moduleLogger } from "../Logger";
import type { ApiClient } from "../api/ApiClient";
import type { BenefitActivationClient } from "../api/BenefitActivationClient";
import { ActivationTrigger } from "../events/ActivationTrigger";
import type { AddToBasketEvent } from "../events/AddToBasketEvent";
import type { BenefitActivatedEvent } from "../events/BenefitActivatedEvent";
import type { BenefitExpiredEvent } from "../events/BenefitExpiredEvent";
import { Slot } from "../events/Slot";
import type { SlotReadyEvent } from "../events/SlotReadyEvent";
import type { TrackingFeatureStatus } from "../tracking/FeatureTrackingPayload";
import type { TrackingService } from "../tracking/TrackingService";
import type { AppButton } from "../util/AppButton";
import { Slider } from "../util/Slider";
import type { BenefitCache } from "./BenefitCache";
import { ActivationStatus, type DetailviewBenefit, ShowCallToAction } from "./DetailviewBenefit";
import { EXPIRED_TEXT } from "./countdown";
import { buildDetailviewBenefitScarcity } from "./scarcity";
import { eventQBus } from "@otto-ec/global-resources/event-q-bus";
import { registerSeenHandlerOnElement } from "../util/SeenHandler";

const log = moduleLogger.scope("service");

export class BenefitService {
	private benefitTrackingStatus = new Map<string, TrackingFeatureStatus>();

	constructor(
		private readonly benefitCache: BenefitCache,
		private readonly trackingService: TrackingService,
		private readonly apiClient: ApiClient,
		private readonly benefitActivationClient: BenefitActivationClient,
		private readonly appButton: AppButton,
	) {}

	registerEventListener(): Promise<unknown[]> {
		return Promise.all([
			eventQBus.on("ft9.benefit.slotReady", this.onSlotReady.bind(this)),
			eventQBus.once("ft9.benefit.expired", this.onBenefitExpired.bind(this)),
			eventQBus.on("ft9.benefit.sheet.activated", this.onBenefitActivatedBySheet.bind(this)),
			eventQBus.on("ft1.order-core.addToBasketResult", this.onAddToBasket.bind(this)),
		]);
	}

	buildDetailViewContainerElement(expired: boolean, benefit: DetailviewBenefit, headlineText: string, scarcityLine?: string, slot?: string) {
		if (expired) {
			return `<div class="benefit-detailview_partnerbenefit" data-benefit-id-ft9="${benefit.activationBenefitId}">
                        <div class="pl_headline100">${headlineText}</div>
                        <div class="benefit-detailview_partnerbenefit-subline">
                            <span class="benefit-detailview_partnerbenefit-scarcity">${EXPIRED_TEXT}</span>
                        </div>
                    </div>`;
		}
		return `<div class="benefit-detailview_partnerbenefit" style="visibility: hidden" data-benefit-id-ft9="${benefit.activationBenefitId}">
                    <div class="pl_headline100">${headlineText}</div>
                    <div class="benefit-detailview_partnerbenefit-subline">
                      <div class="benefit-detailview_partnerbenefit-subline-left">
                        ${scarcityLine}
                        <span class="js_openInPaliSheet pl_link100--primary" data-sheet-lock-mobile-height="false" data-sheet-url="${benefit.sheetUrl}">Details</span>
                      </div>
                      ${slot}
                    </div>
            </div>`;
	}

	async onSlotReady(event: SlotReadyEvent): Promise<void> {
		const searchParams = new URLSearchParams(window.location.search);
		const autoActivateBenefitId = searchParams.get("benefitId")?.replaceAll("/", "").replaceAll("%2F", "").trim();

		const benefit = await this.benefitCache.getBenefit(event.variationId, event.productId, autoActivateBenefitId);

		switch (event.slotId) {
			case Slot.BOUNCE_LAYER: {
				if (!benefit || !benefit.bounceBenefitId) {
					log.warn(`No benefit found for slotId: ${event.slotId} and variationId ${event.variationId}`);
					return Promise.resolve();
				}

				event.targetElement.innerHTML = await this.apiClient.getBounceCinema(benefit.bounceBenefitId);
				eventQBus.emit("ft9.bounce.init");

				return Promise.resolve();
			}
			case Slot.PARTNER_BENEFIT: {
				if (benefit) {
					this.buildTemplateDetailviewBenefit(benefit, event);
					this.trackDetailviewBenefit(benefit, event);
				} else {
					log.warn(`No DetailviewBenefit found for slotId ${event.slotId} and variationId ${event.variationId}`);
					return Promise.resolve();
				}

				return Promise.resolve();
			}
		}
	}

	onBenefitExpired(event: BenefitExpiredEvent) {
		this.benefitCache.reset();

		const detailviewBenefit = event.benefit;
		event.targetElement.innerHTML = this.buildDetailViewContainerElement(true, detailviewBenefit, detailviewBenefit.deactivatedHeadlineText);

		eventQBus.emit("ft9.benefit.active", {
			id: detailviewBenefit.id,
			active: false,
			variationId: event.variationId,
			trigger: ActivationTrigger.EXPIRED_BENEFIT,
		});
	}

	onBenefitActivatedBySheet(event: BenefitActivatedEvent) {
		if (!event.benefitId) {
			return;
		}
		const benefitContainers = document.getElementsByClassName("benefit-detailview_partnerbenefit");
		if (benefitContainers && benefitContainers.length > 0) {
			const benefitContainer = benefitContainers[0] as HTMLElement;
			const benefitId = benefitContainer.dataset.benefitIdFt9;
			if (benefitId && event.benefitId === benefitId) {
				const sliderInput = document.getElementsByClassName("activation_slider-slider-input");
				if (sliderInput && sliderInput.length > 0) {
					(sliderInput[0] as HTMLElement).click();
					this.benefitCache.reset();
				}
			}
		}
	}

	async onAddToBasket(event: AddToBasketEvent) {
		if (!event.variationId || !event.eventMergeId) {
			return;
		}
		const benefit = await this.benefitCache.getBenefit(event.variationId, undefined);

		if (benefit) {
			/*                                                                                                                                                                                 */
			const benefitTrackingStatus = this.benefitTrackingStatus.get(event.variationId) ?? "loaded";
			this.trackingService.sendDetailviewBenefitMergeEvent(benefit, event.eventMergeId, benefitTrackingStatus);
		}
	}

	/*                */
	buildTemplateDetailviewBenefit(discount: DetailviewBenefit, event: SlotReadyEvent) {
		const slider = new Slider(`_${discount.activationBenefitId}_${event.variationId}`);

		const targetElement = event.targetElement;
		const scarcityLineSpan = buildDetailviewBenefitScarcity(discount, event);

		log.info(`${discount.activationStatus === ActivationStatus.ACTIVATED ? "ACTIVATED " : "DEACTIVATED "}`);

		const activateBenefitSlider = (withAnimation: boolean, trigger: ActivationTrigger) => {
			const activatedSliderHTML = slider.getSliderHtml(true, discount, withAnimation);
			targetElement.innerHTML = this.buildDetailViewContainerElement(false, discount, discount.activatedHeadlineText, scarcityLineSpan, activatedSliderHTML);
			eventQBus.emit("ft9.benefit.active", {
				id: discount.id,
				active: true,
				variationId: event.variationId,
				trigger: trigger,
			});
		};

		const deactivatedBenefitSlider = () => {
			const notActivatedSliderHTML = slider.getSliderHtml(false, discount, false);
			targetElement.innerHTML = this.buildDetailViewContainerElement(false, discount, discount.deactivatedHeadlineText, scarcityLineSpan, notActivatedSliderHTML);
			slider.init(() => {
				if (discount.activationUrl) {
					const errorLoggingPayload = {
						benefit_id: discount.id,
						benefit_active_until: discount.activeUntil,
					};
					this.benefitActivationClient.activateViaUrl(discount.activationUrl, errorLoggingPayload).then((success) => {
						if (success) {
							discount.activationStatus = ActivationStatus.ACTIVATED;
							activateBenefitSlider(true, ActivationTrigger.ACTIVATED_BY_USER);

							/*                                                                             */
							this.benefitCache.reset();
						} else {
							slider.reset(targetElement);
							eventQBus.emit("assets.snackbar.trigger", {
								text: "Das hat leider nicht geklappt. Bitte probiere es noch einmal, danke.",
							});
						}
						this.benefitTrackingStatus.set(event.variationId, "clicked");
						this.trackingService.sendDetailviewBenefitSwipeEvent(discount, success);
					});
				}
			}, targetElement);
		};

		const createButtonToApp = () => {
			const callback = () => {
				if (discount.adjustLink) {
					window.location.assign(discount.adjustLink);
					this.trackingService.sendDetailviewBenefitClickEvent(discount);
				}
			};
			targetElement.innerHTML = this.buildDetailViewContainerElement(false, discount, discount.deactivatedHeadlineText, scarcityLineSpan, this.appButton.getButtonHtml());
			this.appButton.init(callback, targetElement);
		};

		switch (discount.showCallToAction) {
			case ShowCallToAction.ACTIVATED_SLIDER:
				activateBenefitSlider(false, ActivationTrigger.FROM_DB_STORAGE);
				break;
			case ShowCallToAction.DEACTIVATED_SLIDER:
				deactivatedBenefitSlider();
				break;
			case ShowCallToAction.BUTTON_TO_APP:
				createButtonToApp();
				break;
		}
	}

	trackDetailviewBenefit(discount: DetailviewBenefit, event: SlotReadyEvent) {
		this.benefitTrackingStatus.set(event.variationId, "loaded");
		this.trackingService.sendDetailviewBenefitLoadedEvent(discount, event.eventMergeId);
		registerSeenHandlerOnElement(event.targetElement, () => {
			this.benefitTrackingStatus.set(event.variationId, "visible");
			this.trackingService.sendDetailviewBenefitVisibleEvent(discount);
		});
	}
}
